/**
 * CabinetModel — renders a single cabinet as a Three.js group of box meshes.
 * Each panel is a <mesh> with <boxGeometry> + <meshStandardMaterial>.
 */

import { useMemo, useState } from 'react';
import { Html } from '@react-three/drei';
import type { CabinetData, Adjacency } from './types';
import { buildAllPanels } from './geometry';
import { getPanelColor, getPanelRoughness } from './materials';
import HardwareRenderer from './HardwareRenderer';

interface CabinetModelProps {
  cabinet: CabinetData;
  adjacency?: Adjacency;
  selected?: boolean;
  onSelect?: (id: string) => void;
  showHardware?: boolean;
  showDrillHoles?: boolean;
}

export default function CabinetModel({
  cabinet,
  adjacency = { left: false, right: false },
  selected = false,
  onSelect,
  showHardware = false,
  showDrillHoles = false,
}: CabinetModelProps) {
  const [hovered, setHovered] = useState(false);

  const panels = useMemo(
    () => buildAllPanels(cabinet, adjacency),
    [cabinet, adjacency],
  );

  const colors = useMemo(() => ({
    box_color: cabinet.box_color,
    door_color: cabinet.door_color,
    back_color: cabinet.back_color,
    shelf_color: cabinet.shelf_color,
    stretcher_color: cabinet.stretcher_color,
  }), [cabinet]);

  return (
    <group
      position={[cabinet.position_x, 0, cabinet.position_z]}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(cabinet.id);
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
        document.body.style.cursor = 'pointer';
      }}
      onPointerOut={() => {
        setHovered(false);
        document.body.style.cursor = 'auto';
      }}
    >
      {panels.map((panel, idx) => {
        const color = getPanelColor(panel, colors);
        const roughness = getPanelRoughness(panel);

        return (
          <mesh
            key={`${panel.name}-${idx}`}
            position={[panel.x, panel.y, panel.z]}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[panel.width, panel.height, panel.depth]} />
            <meshStandardMaterial
              color={color}
              roughness={roughness}
              metalness={0.05}
              emissive={selected ? '#1a3a5c' : hovered ? '#0d1f30' : '#000000'}
              emissiveIntensity={selected ? 0.3 : hovered ? 0.15 : 0}
            />
          </mesh>
        );
      })}

      {/* Label on hover */}
      {hovered && (
        <Html
          position={[cabinet.width / 2, cabinet.height + 2, cabinet.depth / 2]}
          center
          distanceFactor={80}
          style={{ pointerEvents: 'none' }}
        >
          <div
            style={{
              background: 'rgba(0,0,0,0.85)',
              color: '#fff',
              padding: '4px 8px',
              borderRadius: 4,
              fontSize: 11,
              whiteSpace: 'nowrap',
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            {cabinet.code || cabinet.description}
            <span style={{ color: '#999', marginLeft: 6 }}>
              {cabinet.width}"×{cabinet.height}"×{cabinet.depth}"
            </span>
          </div>
        </Html>
      )}

      {/* Hardware models and drill holes */}
      <HardwareRenderer
        lineItemId={cabinet.id}
        cabinetData={cabinet}
        showHardware={showHardware}
        showDrillHoles={showDrillHoles}
      />
    </group>
  );
}
